/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import { cyrb53Hash } from 'utils/helpers';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import { getBlockedEnvironmentsRequest } from 'modules/monitor/actions';
import {
  BlockedEnvironmentsFields,
  BlockedEnvironmentsResultsFields,
} from 'modules/monitor/interfaces';
import { getCompanyName } from 'utils/tsHelpers';
import { useTableFilters } from 'hooks/useTableFilters';
import styles from './styles';

const initialQuery = {
  offset: 0,
  limit: 50,
  period: '',
  search: '',
};

interface Props {
  isLoading: boolean;
  blockedEnvironmentsData: BlockedEnvironmentsFields;
  modalClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const BlockedEnvironmentsTable = ({
  isLoading,
  blockedEnvironmentsData,
  modalClose,
  selectCompany,
  selectPartner,
}: Props) => {
  const headers = [
    {
      key: '',
      i18n: i18n.t('ACCESS_KEY'),
      centerTitle: true,
    },
    {
      key: 'partner',
      i18n: i18n.t('MONITOR.PARTNER_NAME'),
      order: false,
    },
    {
      key: 'environment',
      i18n: i18n.t('MONITOR.ENVIRONMENT_NAME'),
      order: false,
    },
    {
      key: 'provider',
      i18n: i18n.t('MONITOR.PROVIDER'),
      order: false,
      centerTitle: true,
    },
    {
      key: 'processed',
      i18n: i18n.t('MONITOR.SEC_PROCESSED'),
      order: false,
      centerTitle: true,
    },
  ];

  const useStyles = makeStyles(styles());
  const classes = useStyles();
  const history = useHistory();

  const { handleLimitChange, handlePageChange } = useTableFilters(initialQuery);

  const redirectToContext = (item: BlockedEnvironmentsResultsFields) => {
    selectCompany({
      id: item?.company.uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      id: item?.partner.uuid,
      name: item?.partner.name,
      shortName: item?.partner.name,
    });
    history.push(`/environments/${item?.environment.uuid}/instances`);
  };

  return (
    <Grid className={classes.table}>
      <Grid className={classes.titleBackground}>
        <Typography className={classes.tableTitle}>
          {i18n.t('MONITOR.BLOCKED_ENVIRONMENTS_TITLE')}
        </Typography>
      </Grid>
      <TableItems
        header={
          <>
            {headers.map(item => (
              <TableCell centerTitle={item?.centerTitle} key={item?.key}>
                {item?.i18n}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: BlockedEnvironmentsResultsFields) => (
          <TableRow key={cyrb53Hash(item?.environment?.name)}>
            <TableCell centerTitle textAlignCenter>
              <IconButton
                className={classes.accessButton}
                onClick={() => {
                  modalClose();
                  redirectToContext(item);
                }}
              >
                <InputIcon />
              </IconButton>
            </TableCell>
            <TableCell className={classes.text}>
              <Tooltip title={item?.partner.name}>
                <Typography>{item?.partner.name}</Typography>
              </Tooltip>
            </TableCell>
            <TableCell className={classes.text}>
              <Tooltip title={item?.environment.name}>
                <Typography>{item?.environment.name}</Typography>
              </Tooltip>
            </TableCell>
            <TableCell centerTitle>{item?.environment.provider}</TableCell>
            <TableCell centerTitle>{item?.elapsed_blocking_time}</TableCell>
          </TableRow>
        )}
        data={blockedEnvironmentsData}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={
          <Typography className={classes.notFoundText}>
            {i18n.t('MONITOR.NO_BLOCKED_ENVIRONMENTS')}
          </Typography>
        }
        notFooter
      />
    </Grid>
  );
};

const mapStateToProps = ({ monitor: { isLoading } }: ApplicationState) => ({
  isLoading,
});

export default connect(mapStateToProps, {
  getBlockedEnvironmentsRequest,
  modalClose,
  selectCompany,
  selectPartner,
})(BlockedEnvironmentsTable);
